import { createContext, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from "react";

interface ConfirmOptions {
  title?: string;
  prompt: string;
  confirmLabel?: string;
  cancelLabel?: string;
  tone?: "default" | "destructive";
}

interface PromptOptions extends ConfirmOptions {
  // Collect a free-text value (e.g. an audit reason) as part of confirming.
  label?: string;
  placeholder?: string;
  defaultValue?: string;
  required?: boolean; // default true
  multiline?: boolean;
}

type ConfirmFn = (opts: ConfirmOptions) => Promise<boolean>;
type PromptFn = (opts: PromptOptions) => Promise<string | null>;

interface ConfirmContextValue {
  confirm: ConfirmFn;
  prompt: PromptFn;
}

const ConfirmCtx = createContext<ConfirmContextValue | null>(null);

interface PendingState {
  opts: PromptOptions;
  isPrompt: boolean;
  resolve: (v: boolean | string | null) => void;
}

export function ConfirmProvider({ children }: { children: ReactNode }) {
  const [pending, setPending] = useState<PendingState | null>(null);

  const confirm = useCallback<ConfirmFn>(
    (opts) =>
      new Promise<boolean>((resolve) =>
        setPending({ opts, isPrompt: false, resolve: (v) => resolve(v as boolean) }),
      ),
    [],
  );

  const prompt = useCallback<PromptFn>(
    (opts) =>
      new Promise<string | null>((resolve) =>
        setPending({ opts, isPrompt: true, resolve: (v) => resolve(v as string | null) }),
      ),
    [],
  );

  return (
    <ConfirmCtx.Provider value={{ confirm, prompt }}>
      {children}
      {pending && (
        <ConfirmDialog
          opts={pending.opts}
          isPrompt={pending.isPrompt}
          onCancel={() => {
            pending.resolve(pending.isPrompt ? null : false);
            setPending(null);
          }}
          onConfirm={(value) => {
            pending.resolve(pending.isPrompt ? value : true);
            setPending(null);
          }}
        />
      )}
    </ConfirmCtx.Provider>
  );
}

export function useConfirm(): ConfirmFn {
  const ctx = useContext(ConfirmCtx);
  if (!ctx) throw new Error("useConfirm must be used inside <ConfirmProvider>");
  return ctx.confirm;
}

export function usePrompt(): PromptFn {
  const ctx = useContext(ConfirmCtx);
  if (!ctx) throw new Error("usePrompt must be used inside <ConfirmProvider>");
  return ctx.prompt;
}

function ConfirmDialog({
  opts,
  isPrompt,
  onCancel,
  onConfirm,
}: {
  opts: PromptOptions;
  isPrompt: boolean;
  onCancel: () => void;
  onConfirm: (value: string) => void;
}) {
  const ref = useRef<HTMLDialogElement>(null);
  const cancelRef = useRef<HTMLButtonElement>(null);
  const okRef = useRef<HTMLButtonElement>(null);
  const inputRef = useRef<HTMLInputElement | HTMLTextAreaElement | null>(null);
  const previouslyFocused = useRef<Element | null>(null);
  const [value, setValue] = useState(opts.defaultValue ?? "");

  const required = isPrompt && opts.required !== false;
  const canConfirm = !required || value.trim().length > 0;

  // Open the dialog on mount; restore focus when it closes.
  useEffect(() => {
    const d = ref.current;
    if (!d) return;
    previouslyFocused.current = document.activeElement;
    d.showModal();
    if (isPrompt) inputRef.current?.focus();
    else if (opts.tone === "destructive") cancelRef.current?.focus();
    else okRef.current?.focus();

    const onCancelEvt = (e: Event) => {
      e.preventDefault();
      onCancel();
    };
    d.addEventListener("cancel", onCancelEvt);
    return () => {
      d.removeEventListener("cancel", onCancelEvt);
      if (d.open) d.close();
      const prev = previouslyFocused.current;
      if (prev instanceof HTMLElement) {
        try {
          prev.focus();
        } catch {
          /* ignore */
        }
      }
    };
  }, [opts.tone, onCancel, isPrompt]);

  const submit = () => {
    if (canConfirm) onConfirm(value.trim());
  };

  return (
    <dialog ref={ref} className="confirm-dialog" aria-labelledby="confirm-title" aria-describedby="confirm-prompt">
      <div className="confirm-body">
        <div className="confirm-icon" aria-hidden>
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
            <path
              d="M12 9v4M12 17v.01M4.93 19h14.14c1.54 0 2.5-1.67 1.73-3L13.73 4a2 2 0 0 0-3.46 0L3.2 16c-.77 1.33.19 3 1.73 3z"
              stroke="currentColor"
              strokeWidth="1.6"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </div>
        <h2 className="confirm-title" id="confirm-title">
          {opts.title ?? "Confirm action"}
        </h2>
        <p className="confirm-prompt" id="confirm-prompt">
          {opts.prompt}
        </p>
        {isPrompt && (
          <label className="confirm-input-label">
            {opts.label && <span className="confirm-input-text">{opts.label}</span>}
            {opts.multiline ? (
              <textarea
                ref={(el) => {
                  inputRef.current = el;
                }}
                className="confirm-input"
                rows={3}
                placeholder={opts.placeholder}
                value={value}
                onChange={(e) => setValue(e.target.value)}
              />
            ) : (
              <input
                ref={(el) => {
                  inputRef.current = el;
                }}
                className="confirm-input"
                type="text"
                placeholder={opts.placeholder}
                value={value}
                onChange={(e) => setValue(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    submit();
                  }
                }}
              />
            )}
          </label>
        )}
        <div className="confirm-actions">
          <button ref={cancelRef} type="button" className="confirm-cancel" onClick={onCancel}>
            {opts.cancelLabel ?? "Cancel"}
          </button>
          <button
            ref={okRef}
            type="button"
            disabled={!canConfirm}
            className={`primary confirm-ok${opts.tone === "destructive" ? " is-destructive" : ""}`}
            onClick={submit}
          >
            {opts.confirmLabel ?? "Confirm"}
          </button>
        </div>
      </div>
    </dialog>
  );
}
